"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useState } from "react";
import { MessageSquare, Tag, Network, Plus, CornerDownLeft, CircleCheck, Circle, Ellipsis, Calendar, Clock } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
type FeatureId = 'nlp' | 'tags' | 'tree' | null;
const features = [{
  id: 'nlp' as FeatureId,
  title: 'WhatsApp Recovery',
  description: 'Instantly catch missed calls with automated WhatsApp responses that start the conversation.',
  icon: MessageSquare,
  offset: 'lg:translate-x-0'
}, {
  id: 'tags' as FeatureId,
  title: 'Multi-Channel Sync',
  description: 'WhatsApp, Instant WhatsApp Recovery, and Google messages unified into one high-speed dashboard for trades.',
  icon: Tag,
  offset: 'lg:-translate-x-12'
}, {
  id: 'tree' as FeatureId,
  title: 'Lead Pipeline',
  description: 'Keep track of every site visit and quote from first contact to final invoice in one view.',
  icon: Network,
  offset: 'lg:-translate-x-4'
}];
export default function DailyWorkflowPreview() {
  const [activeFeature, setActiveFeature] = useState<FeatureId>(null);
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="82488" id="one-inbox-total-control" className="relative bg-background dark py-24 lg:py-32 overflow-hidden radiating-pattern" ref={sectionRef}>
      {}
      <div className="absolute top-1/2 left-1/4 -translate-y-1/2 w-[600px] h-[600px] bg-primary/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-secondary/10 rounded-full blur-3xl pointer-events-none" />

      <div className="container mx-auto px-4 relative z-10">
        <div className={cn("max-w-3xl mx-auto text-center mb-16 transition-all duration-1000 transform", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10")}>
          <h2 className="h2 text-3xl md:text-4xl lg:text-5xl text-foreground mb-6">
            One Inbox, Total Control
          </h2>
          <p className="text-lg text-muted-foreground font-sans">
            Stop juggling apps between jobs. Our Unified Inbox brings WhatsApp, Instant WhatsApp Recovery, and Google leads into 
            one rugged interface designed for the tools.
          </p>
        </div>

        <div className="grid lg:grid-cols-12 gap-8 lg:gap-0 items-center max-w-6xl mx-auto">
          
          {}
          <div className={cn("lg:col-span-8 relative z-10 transition-all duration-1000 delay-200 transform", isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-10")}>
            <div className="bg-card/80 backdrop-blur-md metallic-border rounded-md shadow-2xl overflow-hidden flex flex-col h-[550px] ring-1 ring-primary/20">
              {}
              <div className="h-12 border-b border-border flex items-center px-4 gap-2 bg-muted/50">
                <div className="flex gap-1.5">
                  <div className="size-3 rounded-full bg-destructive" />
                  <div className="size-3 rounded-full bg-chart-4" />
                  <div className="size-3 rounded-full bg-primary" />
                </div>
                <div className="mx-auto text-xs font-bold text-muted-foreground flex items-center gap-2 tracking-wider uppercase">
                  <CircleCheck className="size-3 text-primary" /> ThinkLocalPro Dashboard
                </div>
              </div>

              {}
              <div className="p-6 flex-1 flex flex-col gap-8 bg-background/50 overflow-y-auto">
                
                {}
                <div className={cn("flex items-center gap-3 p-4 rounded-md border bg-card transition-all duration-500 ease-out", activeFeature === 'nlp' ? "ring-2 ring-primary border-primary shadow-lg shadow-primary/20 scale-[1.02] -translate-y-1" : "border-border shadow-sm")}>
                  <Plus className="text-primary size-5 shrink-0" />
                  <div className="flex-1 flex items-center gap-2">
                    <span className="text-foreground font-bold font-sans">Emergency Leak: 12 Park Rd</span>
                    <span className="text-primary bg-primary/10 px-2 py-0.5 rounded text-xs font-bold flex items-center gap-1">
                      <SafeHydrate><Calendar className="size-3" /></SafeHydrate> Missed Call
                    </span>
                    <span className="text-chart-4 bg-chart-4/10 px-2 py-0.5 rounded text-xs font-bold flex items-center gap-1">
                      <Clock className="size-3" /> 2m ago
                    </span>
                  </div>
                  <CornerDownLeft className="text-muted-foreground size-4 shrink-0" />
                </div>

                {}
                <div className="flex flex-col gap-6">
                  
                  {}
                  <div className="flex items-start gap-4 group">
                    <Circle className="size-5 text-muted-foreground mt-0.5 shrink-0" />
                    <div className="flex-1 border-b border-border pb-6">
                      <div className="flex items-center justify-between">
                        <p className="text-foreground font-bold font-sans">Customer: Dave (Electrician)</p>
                        <Ellipsis className="size-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                      
                      {}
                      <div className={cn("flex gap-2 mt-3 transition-all duration-500 ease-out rounded-md p-1.5 -ml-1.5", activeFeature === 'tags' ? "ring-2 ring-primary bg-primary/5 shadow-md shadow-primary/10 scale-[1.02]" : "")}>
                        <span className="text-xs px-2.5 py-1 rounded-md bg-primary/20 text-primary font-bold uppercase tracking-tight">WhatsApp</span>
                        <span className="text-xs px-2.5 py-1 rounded-md bg-chart-2/20 text-chart-2 font-bold uppercase tracking-tight">High Priority</span>
                        <span className="text-xs px-2.5 py-1 rounded-md bg-muted text-muted-foreground font-bold uppercase tracking-tight">Gloucester</span>
                      </div>
                    </div>
                  </div>

                  {}
                  <div className="flex items-start gap-4 group">
                    <Circle className="size-5 text-muted-foreground mt-0.5 shrink-0" />
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <p className="text-foreground font-bold font-sans">Job: Full Kitchen Rewire</p>
                        <Ellipsis className="size-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                      
                      {}
                      <div className={cn("mt-4 pl-5 border-l-2 flex flex-col gap-3 transition-all duration-500 ease-out rounded-r-md p-3 -ml-3", activeFeature === 'tree' ? "border-primary ring-2 ring-primary bg-primary/5 shadow-md shadow-primary/10 scale-[1.02]" : "border-muted")}>
                        <div className="flex items-center gap-3">
                          <CircleCheck className="size-4 text-primary shrink-0" />
                          <span className="text-sm text-muted-foreground line-through font-sans">Initial inquiry received</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <Circle className="size-4 text-primary shrink-0" />
                          <span className="text-sm text-foreground font-bold font-sans">Auto-reply sent via WhatsApp</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <Circle className="size-4 text-muted-foreground shrink-0" />
                          <span className="text-sm text-foreground font-sans">Awaiting customer reply</span>
                        </div>
                      </div>
                    </div>
                  </div>

                </div>
              </div>
            </div>
          </div>

          {}
          <div className={cn("lg:col-span-4 flex flex-col gap-4 relative z-20 lg:-ml-8 transition-all duration-1000 delay-400 transform", isVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-10")}>
            {features.map((feature, i) => {
            const Icon = feature.icon;
            const isActive = activeFeature === feature.id;
            return <Card key={feature.id} data-index={i} onMouseEnter={() => setActiveFeature(feature.id)} onMouseLeave={() => setActiveFeature(null)} className={cn("glass-panel p-0 cursor-pointer transition-all duration-300 shadow-lg hover:shadow-xl", feature.offset, isActive ? "ring-2 ring-primary border-primary -translate-y-1 scale-105 z-30" : "hover:-translate-y-0.5 z-20")}>
                  <CardHeader className="p-5 pb-2 flex flex-row items-center gap-4 space-y-0">
                    <div className={cn("p-2 rounded-md transition-colors duration-300", isActive ? "bg-primary text-primary-foreground" : "bg-muted text-primary")}>
                      <Icon className="size-5" />
                    </div>
                    <CardTitle className="text-base font-serif font-bold uppercase tracking-tight">{feature.title}</CardTitle>
                  </CardHeader>
                  <CardContent className="p-5 pt-2">
                    <CardDescription className="text-sm leading-relaxed text-muted-foreground font-sans">
                      {feature.description}
                    </CardDescription>
                  </CardContent>
                </Card>;
          })}
          </div>

        </div>
      </div>
    </section>;
}